import { BookingStatus } from '@/types';
import type { AdminBookingAttentionReason } from '@/types';

export type AdminBookingTabKey =
  | 'all'
  | 'attention'
  | 'unpaid'
  | 'active'
  | 'awaiting'
  | 'disputed'
  | 'completed'
  | 'closed';

export interface AdminBookingTab {
  key: AdminBookingTabKey;
  label: string;
  statuses?: BookingStatus[];
  attention?: boolean;
}

export const ADMIN_BOOKING_TABS: AdminBookingTab[] = [
  { key: 'all', label: 'All' },
  { key: 'attention', label: 'Needs attention', attention: true },
  { key: 'unpaid', label: 'Unpaid', statuses: [BookingStatus.PAYMENT_PENDING] },
  {
    key: 'active',
    label: 'Active',
    statuses: [BookingStatus.CONFIRMED, BookingStatus.UPCOMING, BookingStatus.IN_PROGRESS],
  },
  {
    key: 'awaiting',
    label: 'Awaiting confirmation',
    statuses: [BookingStatus.WAITING_FOR_TRAVELER_CONFIRMATION],
  },
  { key: 'disputed', label: 'Disputed', statuses: [BookingStatus.DISPUTED] },
  { key: 'completed', label: 'Completed', statuses: [BookingStatus.COMPLETED] },
  {
    key: 'closed',
    label: 'Cancelled / Expired / Refunded',
    statuses: [BookingStatus.CANCELLED, BookingStatus.EXPIRED, BookingStatus.REFUNDED],
  },
];

export const ATTENTION_LABELS: Record<AdminBookingAttentionReason, string> = {
  disputed: 'Disputed',
  confirmation_overdue: 'Confirmation overdue',
  stuck_payment_pending: 'Paid, not confirmed',
  start_overdue: 'Trip never started',
  paid_but_cancelled: 'Refund missing',
};

export const ATTENTION_DESCRIPTIONS: Record<AdminBookingAttentionReason, string> = {
  disputed: 'The traveler raised a dispute. Resolve it in Finance → Disputes.',
  confirmation_overdue:
    "The traveler's confirmation deadline passed but the trip was not auto-completed.",
  stuck_payment_pending:
    'A payment succeeded more than 30 minutes ago but the booking is still awaiting payment.',
  start_overdue: 'The travel date passed over a day ago but the trip never moved to in progress.',
  paid_but_cancelled:
    'The booking was cancelled with a refund owed, but the payment is still captured.',
};

export const AUDIT_ACTION_LABELS: Record<string, string> = {
  booking_completed: 'Booking completed',
  booking_cancelled: 'Booking cancelled',
  booking_disputed: 'Dispute raised',
  dispute_resolved: 'Dispute resolved',
  refund_processed: 'Refund submitted',
};

export function shortBookingId(id: string) {
  return `#${id.slice(-8).toUpperCase()}`;
}

export function formatDate(value?: string, withTime = false) {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
}

/** Travel dates are stored as UTC midnight of the calendar day. */
export function formatTravelDate(value: string) {
  return new Date(value).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
